import * as XLSX from 'xlsx';
import {
  AUTO_COLUMN_KEYS,
  EnquiryColumnDef,
  EnquiryColumnGroup,
  EnquiryImportConfig,
  groupLabel,
  orderedEnabledColumns,
} from './enquiryColumns';

/**
 * Builds the import template from the tenant's enabled column set, so the sheet
 * a user downloads always matches exactly what the ingestor will read.
 */

const SAMPLE_BY_TYPE: Record<string, string> = {
  date: '2005-03-15',
  email: 'john@example.com',
  phone: '9876543210',
  number: '1',
  select: 'M',
  text: '',
};

const SAMPLE_BY_KEY: Record<string, string> = {
  salutation: 'Mr',
  firstname: 'John',
  lastname: 'Doe',
  middlename: '',
  gender: 'M',
  dateofbirth: '2005-03-15',
  mobile: '9876543210',
  telephone: '04012345678',
  email: 'john@example.com',
  enquirycentre: 'Hyderabad',
  enquiryprogram: 'PU1',
  enquirycourse: 'CSE',
  yearid: '2026-2027',
  batch: 'BATCH-A',
  studentcode: 'AUTO',
  studentcode2: 'AUTO',
  username: 'AUTO',
  password: 'AUTO',
  city: 'Hyderabad',
  contactstate: 'Telangana',
  contactcountry: 'India',
  pincode: '500001',
  contactstreet1: '123 Main St',
  fathername: 'Robert Doe',
  fathermobile: '9876543211',
  fatheremail: 'robert@example.com',
  mothername: 'Jane Doe',
  mothermobile: '9876543212',
  motheremail: 'jane@example.com',
};

const sampleFor = (column: EnquiryColumnDef): string =>
  SAMPLE_BY_KEY[column.key] ?? SAMPLE_BY_TYPE[column.type] ?? '';

export function generateEnquiryTemplate(
  columns: EnquiryColumnDef[],
  groups: EnquiryColumnGroup[],
  config: EnquiryImportConfig,
): void {
  const selected = orderedEnabledColumns(columns, config);
  if (selected.length === 0) return;

  const mandatory = new Set(config.mandatoryColumns);
  const wb = XLSX.utils.book_new();

  // Row 1: headers. Row 2: a filled example. Row 3: mandatory values only, so
  // the user can see the minimum a row needs.
  const headerRow = selected.map(c => c.label);
  const exampleRow = selected.map(c => sampleFor(c));
  const minimalRow = selected.map(c => (mandatory.has(c.key) ? sampleFor(c) : ''));

  const sheet = XLSX.utils.aoa_to_sheet([headerRow, exampleRow, minimalRow]);
  sheet['!cols'] = selected.map(c => ({ wch: Math.max(14, Math.min(30, c.label.length + 4)) }));
  XLSX.utils.book_append_sheet(wb, sheet, 'Enquiries');

  const instructions: string[][] = [
    ['Column', 'Group', 'Required', 'Type', 'Notes', 'Stored in'],
    ...selected.map(c => [
      c.label,
      groupLabel(groups, c.group),
      mandatory.has(c.key) ? 'Yes' : 'No',
      c.type,
      [
        c.description ?? '',
        AUTO_COLUMN_KEYS.includes(c.key) ? 'Supports AUTO.' : '',
        c.aliases?.length ? `Also accepted: ${c.aliases.join(', ')}.` : '',
        c.writeTarget === null ? 'Read but not stored yet.' : '',
      ]
        .filter(Boolean)
        .join(' '),
      c.writeTarget ?? '—',
    ]),
  ];

  const instrSheet = XLSX.utils.aoa_to_sheet(instructions);
  instrSheet['!cols'] = [{ wch: 32 }, { wch: 20 }, { wch: 10 }, { wch: 10 }, { wch: 60 }, { wch: 46 }];
  XLSX.utils.book_append_sheet(wb, instrSheet, 'Instructions');

  XLSX.writeFile(wb, 'enquiry-import-template.xlsx');
}
